
import { motion } from 'framer-motion';
import { useState, useMemo } from 'react';

export default function BeforeAfter() {
  const [activeMetric, setActiveMetric] = useState(0);

  const handleStartTrial = () => {
    // Scroll vers la section pricing
    const pricingSection = document.getElementById('pricing');
    if (pricingSection) {
      pricingSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const metrics = useMemo(() => [
    {
      id: 0,
      title: 'Taux de Conversion',
      icon: 'ri-shopping-cart-line',
      color: '#14B8A6',
      before: {
        value: 2.3,
        label: '2.3%',
        description: 'Conversion moyenne sans agent IA'
      },
      after: {
        value: 8.7,
        label: '8.7%',
        description: 'Conversion avec agent IA'
      },
      improvement: '+278%',
      details: [
        'Réponses instantanées 24/7',
        'Recommandations personnalisées',
        'Accompagnement jusqu\'à l\'achat'
      ]
    },
    {
      id: 1,
      title: 'Temps de Réponse',
      icon: 'ri-time-line',
      color: '#8B5CF6',
      before: {
        value: 45,
        label: '45 min',
        description: 'Délai moyen de réponse humaine'
      },
      after: {
        value: 0.5,
        label: '< 1 sec',
        description: 'Réponse instantanée de l\'agent'
      },
      improvement: '-99%',
      details: [
        'Disponibilité immédiate',
        'Pas de file d\'attente',
        'Traitement simultané illimité'
      ]
    },
    {
      id: 2,
      title: 'Coûts Support Client',
      icon: 'ri-money-euro-circle-line',
      color: '#F59E0B',
      before: {
        value: 12500,
        label: '12 500€/mois',
        description: 'Coût équipe support traditionnelle'
      },
      after: {
        value: 3200,
        label: '3 200€/mois',
        description: 'Coût avec agent IA'
      },
      improvement: '-74%',
      details: [
        'Réduction des effectifs nécessaires',
        'Automatisation des tâches répétitives',
        'Scalabilité sans coût additionnel'
      ]
    },
    {
      id: 3,
      title: 'Satisfaction Client',
      icon: 'ri-emotion-happy-line',
      color: '#EC4899',
      before: {
        value: 72,
        label: '72%',
        description: 'Taux de satisfaction moyen'
      },
      after: {
        value: 94,
        label: '94%',
        description: 'Satisfaction avec agent IA'
      },
      improvement: '+31%',
      details: [
        'Réponses précises et cohérentes',
        'Pas de frustration d\'attente',
        'Expérience personnalisée'
      ]
    },
    {
      id: 4,
      title: 'Volume Traité',
      icon: 'ri-chat-3-line',
      color: '#10B981',
      before: {
        value: 150,
        label: '150/jour',
        description: 'Demandes traitées par équipe'
      },
      after: {
        value: 2500,
        label: '2 500/jour',
        description: 'Demandes traitées par agent IA'
      },
      improvement: '+1567%',
      details: [
        'Capacité illimitée',
        'Pas de limite horaire',
        'Gestion multi-conversations'
      ]
    },
    {
      id: 5,
      title: 'Panier Moyen',
      icon: 'ri-shopping-bag-line',
      color: '#EF4444',
      before: {
        value: 85,
        label: '85€',
        description: 'Valeur moyenne sans assistance'
      },
      after: {
        value: 187,
        label: '187€',
        description: 'Valeur avec recommandations IA'
      },
      improvement: '+120%',
      details: [
        'Upselling intelligent',
        'Cross-selling pertinent',
        'Bundles personnalisés'
      ]
    }
  ], []);

  const currentMetric = metrics[activeMetric];
  const beforePercentage = currentMetric.id === 1 ? 95 : (currentMetric.before.value / Math.max(currentMetric.before.value, currentMetric.after.value)) * 100;
  const afterPercentage = currentMetric.id === 1 ? 5 : (currentMetric.after.value / Math.max(currentMetric.before.value, currentMetric.after.value)) * 100;

  return (
    <section className="py-24 px-6 bg-gradient-to-b from-white via-gray-50 to-white relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-5 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-teal-500 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500 rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="inline-block px-4 py-2 bg-gradient-to-r from-teal-500/10 to-purple-500/10 backdrop-blur-sm rounded-full border border-teal-500/20 mb-6">
            <span className="text-sm font-semibold uppercase tracking-wider text-teal-600">
              Impact Mesurable
            </span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold mb-6 text-gray-900">
            Avant / Après : La Transformation
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Découvrez l'impact réel de nos Agents IA sur les métriques clés de votre business
          </p>
        </motion.div>

        {/* Metrics selector */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-12">
          {metrics.map((metric) => (
            <button
              key={metric.id}
              onClick={() => setActiveMetric(metric.id)}
              className={`p-4 rounded-2xl transition-all cursor-pointer will-change-transform ${
                activeMetric === metric.id
                  ? 'bg-white shadow-xl scale-105'
                  : 'bg-white/50 hover:bg-white hover:shadow-lg'
              }`}
            >
              <div 
                className="w-12 h-12 flex items-center justify-center rounded-xl mb-3 mx-auto"
                style={{ 
                  backgroundColor: activeMetric === metric.id ? `${metric.color}20` : '#f3f4f6'
                }}
              >
                <i 
                  className={`${metric.icon} text-2xl`}
                  style={{ color: activeMetric === metric.id ? metric.color : '#6b7280' }}
                ></i>
              </div>
              <div className={`text-sm font-semibold text-center ${
                activeMetric === metric.id ? 'text-gray-900' : 'text-gray-600'
              }`}>
                {metric.title}
              </div>
            </button>
          ))}
        </div>

        {/* Main comparison */}
        <motion.div
          key={activeMetric}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-3xl shadow-2xl overflow-hidden"
        >
          {/* Header with improvement badge */}
          <div className="p-8 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div 
                  className="w-16 h-16 flex items-center justify-center rounded-2xl"
                  style={{ backgroundColor: `${currentMetric.color}20` }}
                >
                  <i className={`${currentMetric.icon} text-3xl`} style={{ color: currentMetric.color }}></i>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">{currentMetric.title}</h3>
                  <p className="text-gray-600">Comparaison des performances</p>
                </div>
              </div>
              <div 
                className="px-6 py-3 rounded-full font-bold text-lg text-white shadow-lg"
                style={{ backgroundColor: currentMetric.color }}
              >
                {currentMetric.improvement}
              </div>
            </div>
          </div>

          {/* Visual comparison */}
          <div className="p-8">
            <div className="grid lg:grid-cols-2 gap-8 mb-8">
              {/* Before */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="relative"
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-red-100">
                    <i className="ri-close-line text-xl text-red-600"></i>
                  </div>
                  <h4 className="text-xl font-bold text-gray-900">Avant</h4>
                </div>

                {/* Bar chart */}
                <div className="mb-6">
                  <div className="h-48 bg-gray-100 rounded-2xl overflow-hidden relative">
                    <motion.div
                      initial={{ height: 0 }}
                      animate={{ height: `${beforePercentage}%` }}
                      transition={{ duration: 1, delay: 0.3 }}
                      className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-red-500 to-red-400 flex items-end justify-center pb-4 will-change-[height]"
                    >
                      <span className="text-3xl font-bold text-white">
                        {currentMetric.before.label}
                      </span>
                    </motion.div>
                  </div>
                </div>

                <div className="p-4 bg-red-50 rounded-xl border border-red-100">
                  <p className="text-sm text-gray-700 font-medium">
                    {currentMetric.before.description}
                  </p>
                </div>
              </motion.div>

              {/* After */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="relative"
              >
                <div className="flex items-center gap-3 mb-6">
                  <div 
                    className="w-10 h-10 flex items-center justify-center rounded-full"
                    style={{ backgroundColor: `${currentMetric.color}20` }}
                  >
                    <i className="ri-check-line text-xl" style={{ color: currentMetric.color }}></i>
                  </div>
                  <h4 className="text-xl font-bold text-gray-900">Après</h4>
                </div>

                {/* Bar chart */}
                <div className="mb-6">
                  <div className="h-48 bg-gray-100 rounded-2xl overflow-hidden relative">
                    <motion.div
                      initial={{ height: 0 }}
                      animate={{ height: `${afterPercentage}%` }}
                      transition={{ duration: 1, delay: 0.4 }}
                      className="absolute bottom-0 left-0 right-0 flex items-end justify-center pb-4 will-change-[height]"
                      style={{ 
                        background: `linear-gradient(to top, ${currentMetric.color}, ${currentMetric.color}dd)`
                      }}
                    >
                      <span className="text-3xl font-bold text-white">
                        {currentMetric.after.label}
                      </span>
                    </motion.div>
                  </div>
                </div>

                <div 
                  className="p-4 rounded-xl border"
                  style={{ 
                    backgroundColor: `${currentMetric.color}10`,
                    borderColor: `${currentMetric.color}30`
                  }}
                >
                  <p className="text-sm text-gray-700 font-medium">
                    {currentMetric.after.description}
                  </p>
                </div>
              </motion.div>
            </div>

            {/* Details */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="p-6 bg-gradient-to-br from-gray-50 to-white rounded-2xl border border-gray-100"
            >
              <h5 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                <i className="ri-lightbulb-line text-xl" style={{ color: currentMetric.color }}></i>
                Comment nous y parvenons
              </h5>
              <ul className="grid md:grid-cols-3 gap-4">
                {currentMetric.details.map((detail, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <i 
                      className="ri-checkbox-circle-fill text-xl flex-shrink-0 mt-0.5"
                      style={{ color: currentMetric.color }}
                    ></i>
                    <span className="text-gray-700">{detail}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-16 text-center"
        >
          <div className="inline-flex flex-col sm:flex-row items-center gap-4 p-8 bg-gradient-to-r from-gray-900 to-gray-700 rounded-3xl shadow-2xl">
            <div className="text-left">
              <h3 className="text-2xl font-bold text-white mb-2">
                Prêt à transformer vos métriques ?
              </h3>
              <p className="text-gray-300">
                Démarrez votre essai gratuit et constatez les résultats en 30 jours
              </p>
            </div>
            <button 
              onClick={handleStartTrial}
              className="px-8 py-4 bg-white text-gray-900 rounded-full font-bold text-lg hover:bg-gray-100 transition-all whitespace-nowrap cursor-pointer shadow-lg hover:scale-105 will-change-transform"
            >
              Essayer Gratuitement
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
